import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, RefreshCw, Search } from 'lucide-react';
import { Modal, Table, toast } from '../../ui';
import invoiceService from '../../services/invoiceService';
import { transactionsAPI } from './services/api';
import { formatDateTime, formatINR } from '../../utils/format';
import { getErrorMessage } from '../../utils/apiError';
import {
  AdminHeader,
  CopyButton,
  LoadError,
  PaymentReviewDialog,
  ScreenshotLink,
  StateBadge,
  StatTile,
  lookup,
  needsPaymentReview,
  proofSubmittedAt,
  providerLabel,
  resolvePaymentState,
  statusTitleLabel,
} from './pages/orderAdmin';

const STATE_FILTERS = [
  { value: '', label: 'All states' },
  { value: 'Submitted', label: 'Needs review' },
  { value: 'Succeeded', label: 'Paid' },
  { value: 'Created,Pending', label: 'Pending' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Refunded', label: 'Refunded' },
];

const METHOD_FILTERS = [
  { value: '', label: 'All methods' },
  { value: 'manual-upi', label: 'UPI QR (manual)' },
  { value: 'phonepe', label: 'PhonePe' },
  { value: 'admin-confirmation', label: 'Confirmed by admin' },
  { value: 'PhonePe', label: 'PhonePe (older payments)' },
  { value: 'QR-CODE', label: 'UPI QR (older payments)' },
];

// <input type="date"> values are local calendar days; the API gets exact instants (end of day is inclusive).
const startOfDay = value => (value ? new Date(`${value}T00:00:00`) : null);
const endOfDay = value => (value ? new Date(`${value}T23:59:59.999`) : null);
const dayAfter = value => {
  const date = startOfDay(value);
  if (date) date.setDate(date.getDate() + 1);
  return date;
};

const TransactionPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [selected, setSelected] = useState(null);
  const [review, setReview] = useState(null); // { payment, approve }
  const [exporting, setExporting] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    const next = searchInput.trim();
    if (next === searchTerm) return undefined;
    const timer = setTimeout(() => {
      setSearchTerm(next);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, searchTerm]);

  const baseFilters = useCallback(
    () => ({
      searchTerm,
      paymentMethod: methodFilter,
      startDate: startOfDay(dateFrom)?.toISOString(),
      endDate: endOfDay(dateTo)?.toISOString(),
    }),
    [searchTerm, methodFilter, dateFrom, dateTo]
  );

  const fetchTransactions = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    try {
      const response = await transactionsAPI.getAll({ ...baseFilters(), state: stateFilter, page, pageSize });
      if (id !== requestId.current) return;
      const data = response.data?.data;
      setTransactions(data?.items || []);
      setTotalItems(data?.totalItems || 0);
    } catch (err) {
      if (id !== requestId.current) return;
      setTransactions([]);
      setTotalItems(0);
      setError(getErrorMessage(err, "Couldn't load transactions"));
    }
    setLoading(false);
  }, [baseFilters, stateFilter, page, pageSize]);

  // Counts and the paid total come from the server for the whole filtered set, not just this page.
  const fetchSummary = useCallback(async () => {
    try {
      const response = await transactionsAPI.getSummary(baseFilters());
      setSummary(response.data?.data || null);
    } catch {
      setSummary(null);
    }
  }, [baseFilters]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const refresh = () => {
    fetchTransactions();
    fetchSummary();
  };

  const changeFilter = setter => event => {
    setter(event.target.value);
    setPage(1);
  };

  const showState = value => {
    setStateFilter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setStateFilter('');
    setMethodFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    const result = await invoiceService.exportCsv(startOfDay(dateFrom), dateTo ? dayAfter(dateTo) : null);
    setExporting(false);
    if (result.success) toast.success('Invoice CSV downloaded');
    else toast.error(result.error);
  };

  const startReview = (payment, approve) => {
    setSelected(null);
    setReview({ payment, approve });
  };

  const count = state => lookup(summary?.byState || {}, state) || 0;
  const hasFilters = Boolean(searchTerm || stateFilter || methodFilter || dateFrom || dateTo);
  const scopeHint = searchTerm || methodFilter || dateFrom || dateTo ? 'Matching the filters' : 'All time';

  const columns = [
    {
      title: 'Payment',
      key: 'payment',
      width: 230,
      render: (_, record) => (
        <div>
          <span className="oa-mono">{record.transactionId || record.id}</span>
          <span className="oa-sub">{formatDateTime(record.paymentDate)}</span>
        </div>
      ),
    },
    {
      title: 'Order',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 190,
      render: orderId =>
        orderId ? (
          <Link className="oa-link oa-mono" to={`/admin/orders/${encodeURIComponent(orderId)}`}>
            {orderId}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 200,
      render: (_, record) => (
        <div>
          <div>{record.userName || '—'}</div>
          <span className="oa-sub">{record.userEmail || record.userPhone || ''}</span>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      align: 'right',
      render: amount => <span className="oa-num oa-strong">{formatINR(amount)}</span>,
    },
    {
      title: 'Method',
      key: 'method',
      width: 150,
      render: (_, record) => providerLabel(record),
    },
    {
      title: 'State',
      key: 'state',
      width: 130,
      render: (_, record) => <StateBadge state={resolvePaymentState(record)} kind="payment" />,
    },
    {
      title: 'Proof',
      key: 'proof',
      width: 200,
      render: (_, record) =>
        record.referenceId || record.screenshotUrl ? (
          <div>
            <span className="oa-mono">{record.referenceId ? `UTR ${record.referenceId}` : 'No UTR'}</span>
            {record.screenshotUrl && (
              <span className="oa-sub">
                <ScreenshotLink url={record.screenshotUrl} />
              </span>
            )}
          </div>
        ) : (
          <span className="oa-muted">—</span>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 230,
      render: (_, record) => (
        <div className="oa-inline">
          {needsPaymentReview(record) && (
            <>
              <button type="button" className="pk-btn pk-btn--primary pk-btn--xs" onClick={() => startReview(record, true)}>
                Approve
              </button>
              <button type="button" className="pk-btn pk-btn--danger pk-btn--xs" onClick={() => startReview(record, false)}>
                Reject
              </button>
            </>
          )}
          <button
            type="button"
            className="pk-btn pk-btn--ghost pk-btn--xs"
            onClick={() => setSelected(record)}
            aria-label={`Details of payment ${record.transactionId || record.id}`}
          >
            Details
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="oa-page">
      <AdminHeader
        title="Transactions"
        sub="Every payment attempt. UPI QR payments are verified by hand: approve them once the money is in your account."
        actions={
          <>
            <button type="button" className="pk-btn pk-btn--ghost pk-btn--sm" onClick={refresh} disabled={loading}>
              <RefreshCw aria-hidden="true" /> Refresh
            </button>
            <button
              type="button"
              className="pk-btn pk-btn--secondary pk-btn--sm"
              onClick={handleExport}
              disabled={exporting}
              aria-busy={exporting || undefined}
              title="GST invoice lines for the selected dates (all dates when none are set)"
            >
              <Download aria-hidden="true" /> Export invoices (CSV)
            </button>
          </>
        }
      />

      {summary && (
        <div className="oa-stats">
          <StatTile
            label="Needs review"
            value={count('Submitted')}
            hint="Show these payments"
            attention={count('Submitted') > 0}
            pressed={stateFilter === 'Submitted'}
            onClick={() => showState(stateFilter === 'Submitted' ? '' : 'Submitted')}
          />
          <StatTile
            label="Paid"
            value={formatINR(summary.succeededAmount)}
            hint={`${count('Succeeded')} payments · ${scopeHint.toLowerCase()}`}
          />
          <StatTile label="Pending" value={count('Created') + count('Pending')} hint="No proof submitted yet" />
          <StatTile label="Failed or rejected" value={count('Failed')} hint={scopeHint} />
        </div>
      )}

      <div className="pk-card oa-panel">
        <div className="oa-toolbar">
          <div className="pk-field oa-grow">
            <label className="pk-label" htmlFor="txn-search">
              Search
            </label>
            <div className="pk-input-icon">
              <Search aria-hidden="true" />
              <input
                id="txn-search"
                type="search"
                className="pk-input oa-sm"
                placeholder="Transaction ID, order ID, UTR or email"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
            </div>
          </div>
          <div className="pk-field">
            <label className="pk-label" htmlFor="txn-state">
              State
            </label>
            <select id="txn-state" className="pk-select oa-sm" value={stateFilter} onChange={changeFilter(setStateFilter)}>
              {STATE_FILTERS.map(option => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="pk-field">
            <label className="pk-label" htmlFor="txn-method">
              Method
            </label>
            <select id="txn-method" className="pk-select oa-sm" value={methodFilter} onChange={changeFilter(setMethodFilter)}>
              {METHOD_FILTERS.map(option => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="pk-field">
            <label className="pk-label" htmlFor="txn-from">
              From
            </label>
            <input
              id="txn-from"
              type="date"
              className="pk-input oa-sm"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={changeFilter(setDateFrom)}
            />
          </div>
          <div className="pk-field">
            <label className="pk-label" htmlFor="txn-to">
              To
            </label>
            <input
              id="txn-to"
              type="date"
              className="pk-input oa-sm"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={changeFilter(setDateTo)}
            />
          </div>
          <button type="button" className="pk-btn pk-btn--ghost pk-btn--sm" onClick={clearFilters} disabled={!hasFilters}>
            Clear filters
          </button>
        </div>
      </div>

      {error ? (
        <LoadError title="Couldn't load transactions" message={error} onRetry={fetchTransactions} />
      ) : (
        <div className="pk-card oa-panel oa-panel--flush">
          <div className="oa-panel__head">
            <h2 className="oa-panel__title">Payments</h2>
            <span className="oa-small oa-muted" aria-live="polite">
              {loading ? 'Loading…' : `${totalItems} ${totalItems === 1 ? 'payment' : 'payments'}`}
            </span>
          </div>
          <Table
            dataSource={transactions}
            columns={columns}
            loading={loading}
            rowKey="id"
            scroll={{ x: 1400 }}
            emptyText={hasFilters ? 'No payments match these filters.' : 'No payments yet.'}
            pagination={{
              total: totalItems,
              current: page,
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: total => `${total} payments`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              },
            }}
          />
        </div>
      )}

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Payment details" footer={null} width={680}>
        {selected && (
          <div className="oa-dialog">
            <dl className="oa-kv">
              <div>
                <dt>State</dt>
                <dd>
                  <StateBadge state={resolvePaymentState(selected)} kind="payment" />
                </dd>
              </div>
              <div>
                <dt>Method</dt>
                <dd>{providerLabel(selected)}</dd>
              </div>
              <div>
                <dt>Amount</dt>
                <dd className="oa-num">{formatINR(selected.amount)}</dd>
              </div>
              <div>
                <dt>Order</dt>
                <dd>
                  {selected.orderId ? (
                    <Link className="oa-link oa-mono" to={`/admin/orders/${encodeURIComponent(selected.orderId)}`}>
                      {selected.orderId}
                    </Link>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt>Transaction ID</dt>
                <dd className="oa-inline">
                  <span className="oa-mono">{selected.transactionId || '—'}</span>
                  <CopyButton value={selected.transactionId} label="Copy transaction ID" />
                </dd>
              </div>
              <div>
                <dt>UTR / reference</dt>
                <dd className="oa-inline">
                  <span className="oa-mono">{selected.referenceId || '—'}</span>
                  <CopyButton value={selected.referenceId} label="Copy UTR" />
                </dd>
              </div>
              <div>
                <dt>Screenshot</dt>
                <dd>
                  <ScreenshotLink url={selected.screenshotUrl} />
                </dd>
              </div>
              <div>
                <dt>Proof submitted</dt>
                <dd>
                  {selected.referenceId || selected.screenshotUrl ? formatDateTime(proofSubmittedAt(selected)) : '—'}
                </dd>
              </div>
              <div>
                <dt>Customer</dt>
                <dd>
                  {selected.userName || '—'}
                  {selected.userEmail && <span className="oa-sub">{selected.userEmail}</span>}
                  {selected.userPhone && <span className="oa-sub">{selected.userPhone}</span>}
                </dd>
              </div>
              <div>
                <dt>Started</dt>
                <dd>{formatDateTime(selected.paymentDate)}</dd>
              </div>
              <div>
                <dt>Completed</dt>
                <dd>{selected.completedAt ? formatDateTime(selected.completedAt) : '—'}</dd>
              </div>
            </dl>

            <div>
              <h3 className="oa-section-title">History</h3>
              {selected.status?.length > 0 ? (
                <ol className="oa-timeline">
                  {selected.status.map((entry, index) => (
                    <li key={`${entry.title}-${index}`}>
                      <div className="oa-timeline__title">{statusTitleLabel(entry.title)}</div>
                      <div className="oa-timeline__meta">{formatDateTime(entry.createAt)}</div>
                      {entry.description && <p className="oa-timeline__note">{entry.description}</p>}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="oa-muted oa-small">No history recorded.</p>
              )}
            </div>

            <div className="oa-dialog-actions">
              {selected.orderId && (
                <Link
                  className="pk-btn pk-btn--secondary pk-btn--sm"
                  to={`/admin/orders/${encodeURIComponent(selected.orderId)}`}
                >
                  Open order
                </Link>
              )}
              {needsPaymentReview(selected) && (
                <>
                  <button type="button" className="pk-btn pk-btn--danger pk-btn--sm" onClick={() => startReview(selected, false)}>
                    Reject
                  </button>
                  <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={() => startReview(selected, true)}>
                    Approve
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      <PaymentReviewDialog
        open={!!review}
        payment={review?.payment}
        approve={review?.approve}
        onClose={() => setReview(null)}
        onDone={refresh}
      />
    </div>
  );
};

export default TransactionPage;
